import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Form, Button, Badge, InputGroup, Spinner, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import {
    fetchPromoBountyAllApi,
    moderatePromoBountyApi,
} from "../../http/userAPI";

const fmtDate = (iso) =>
    iso
        ? new Date(iso)
            .toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            .replace(",", "")
        : "—";

const statusOptions = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
];

const PAGE_SIZE = 20;

export default function AdminPromoBounty() {
    // Filters
    const [status, setStatus] = useState("pending");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    // Data
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // Modal state
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / PAGE_SIZE)), [total]);

    const load = async () => {
        try {
            setLoading(true);
            const params = {
                status: status === "all" ? undefined : status,
                q: query,
                page,
                limit: PAGE_SIZE,
            };
            const data = await fetchPromoBountyAllApi(params);

            const list = (data?.requests || []).map((r) => ({
                id: r.id,
                created_at: r.createdAt || r.created_at,
                user_id: r.user_id,
                user_name: r.user_name,
                user_amz_name: r.user_amz_name,
                platform: r.platform,
                post_url: r.post_url,
                screenshot_path: r.screenshot_path,
                note: r.note,
                is_active_link_claimed: !!r.is_active_link_claimed,
                is_video: !!r.is_video,
                verified_active_link: r.verified_active_link === true,
                verified_video_authorship: r.verified_video_authorship === true,
                estimated_payout: r.estimated_payout,
                final_payout: r.final_payout,
                status: r.status,
                moderator_note: r.moderator_note,
                reviewed_at: r.reviewed_at,
            }));

            setRows(list);
            setTotal(Number.isFinite(data?.total) ? data.total : list.length);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to load promo submissions", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    };

    // Загружаем при смене страницы/статуса
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, status]);

    // При смене статуса — на первую страницу
    useEffect(() => {
        setPage(1);
    }, [status]);

    const onSearch = (e) => {
        e?.preventDefault?.();
        setPage(1);
        load();
    };

    const resetFilters = () => {
        setStatus("pending");
        setQuery("");
        setPage(1);
        load();
    };

    const openEdit = (row) => setEditing({ ...row });
    const closeEdit = () => setEditing(null);

    const approve = async () => {
        if (!editing) return;
        try {
            setSaving(true);
            const payload = {
                id: editing.id,
                status: "approved",
                verified_active_link: !!editing.verified_active_link,
                verified_video_authorship: !!editing.verified_video_authorship,
                moderator_note: editing.moderator_note || "",
                override_final_payout: editing.final_payout ?? undefined,
            };
            await moderatePromoBountyApi(payload);
            toast.success("Approved", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            closeEdit();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Approve failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSaving(false);
        }
    };

    const reject = async () => {
        if (!editing) return;
        try {
            setSaving(true);
            const payload = {
                id: editing.id,
                status: "rejected",
                verified_active_link: !!editing.verified_active_link,
                verified_video_authorship: !!editing.verified_video_authorship,
                moderator_note: editing.moderator_note || "",
                override_final_payout: 0,
            };
            await moderatePromoBountyApi(payload);
            toast.success("Rejected", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            closeEdit();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Reject failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSaving(false);
        }
    };

    const setPending = async () => {
        if (!editing) return;
        try {
            setSaving(true);
            const payload = {
                id: editing.id,
                status: "pending",
                verified_active_link: !!editing.verified_active_link,
                verified_video_authorship: !!editing.verified_video_authorship,
                moderator_note: editing.moderator_note || "",
            };
            await moderatePromoBountyApi(payload);
            toast.success("Moved to Pending", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            closeEdit();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Action failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                <h5 className="text-muted mb-0">Promo Bounty — Admin</h5>
                <Form onSubmit={onSearch} className="d-flex gap-2 flex-wrap align-items-end">
                    <Form.Group>
                        <Form.Label className="small text-muted mb-1">Status</Form.Label>
                        <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                            {statusOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small text-muted mb-1">Search</Form.Label>
                        <InputGroup size="sm">
                            <Form.Control
                                placeholder="URL / user / platform"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <Button type="submit" variant="secondary">
                                Search
                            </Button>
                        </InputGroup>
                    </Form.Group>
                    <div className="d-flex align-items-end gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
                            Reset
                        </Button>
                        <Button variant="outline-primary" size="sm" onClick={load} disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Reload"}
                        </Button>
                    </div>
                </Form>
            </div>

            <div className="w-100" style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 8 }}>
                <hr />
                {loading ? (
                    <div className="text-muted small">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-muted">No submissions.</div>
                ) : (
                    <Table bordered hover size="sm" striped className="mt-1" style={{ minWidth: 1040, lineHeight: 1.2 }}>
                        <thead className="table-light text-muted small">
                        <tr>
                            <th className="text-center small">ID</th>
                            <th className="text-start small">Date</th>
                            <th className="text-start small">UID</th>
                            <th className="text-start small">Username</th>
                            <th className="text-start small">AMZ</th>
                            <th className="text-start small">Platform</th>
                            <th className="text-start small">Post URL</th>
                            <th className="text-center small">Claim</th>
                            <th className="text-center small">Verified</th>
                            <th className="text-center small">Est.</th>
                            <th className="text-center small">Final</th>
                            <th className="text-center small">Status</th>
                            <th className="text-center small">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="small fst-italic">
                                <td className="text-center align-middle text-muted small">{r.id}</td>
                                <td className="text-start align-middle text-muted small">{fmtDate(r.created_at)}</td>
                                <td className="text-start align-middle text-muted small">
                                    <div className="d-flex flex-column">
                                        <span className="text-muted">{r.user_id}</span>
                                    </div>
                                </td>
                                <td className="text-start align-middle text-muted small">
                                    <div className="d-flex flex-column">
                                        <span>{r.user_name || "—"}</span>
                                    </div>
                                </td>
                                <td className="text-start align-middle text-muted small">
                                    <div className="d-flex flex-column">
                                        <span className="text-muted">AMZ: {r.user_amz_name || "—"}</span>
                                    </div>
                                </td>
                                <td className="text-start align-middle text-muted small">{r.platform || "—"}</td>
                                <td
                                    className="text-start align-middle text-muted small"
                                    style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}
                                >
                                    <a href={r.post_url} target="_blank" rel="noopener noreferrer" className="text-decoration-underline">
                                        {r.post_url}
                                    </a>
                                </td>
                                <td className="text-center align-middle text-muted small">
                                    <div>
                                        <Badge bg={r.is_active_link_claimed ? "success" : "secondary"} className="me-1">
                                            Link {r.is_active_link_claimed ? "Yes" : "No"}
                                        </Badge>
                                        <Badge bg={r.is_video ? "primary" : "secondary"}>Video {r.is_video ? "Yes" : "No"}</Badge>
                                    </div>
                                </td>
                                <td className="text-center align-middle text-muted small">
                                    <div>
                                        <Badge bg={r.verified_active_link ? "success" : "secondary"} className="me-1">
                                            Link
                                        </Badge>
                                        <Badge bg={r.verified_video_authorship ? "primary" : "secondary"}>Video</Badge>
                                    </div>
                                </td>
                                <td className="text-center align-middle text-success fw-bold small">
                                    {r.estimated_payout ?? "—"} {r.estimated_payout ? "Virt" : ""}
                                </td>
                                <td className="text-center align-middle text-success fw-bold small">
                                    {typeof r.final_payout === "number" ? `${r.final_payout} Virt` : "—"}
                                </td>
                                <td className="text-center align-middle small">
                    <span
                        className={`text-uppercase fw-bold ${
                            r.status === "approved"
                                ? "text-success"
                                : r.status === "pending"
                                    ? "text-warning"
                                    : r.status === "rejected"
                                        ? "text-danger"
                                        : "text-secondary"
                        }`}
                    >
                      {String(r.status)}
                    </span>
                                </td>
                                <td className="text-center align-middle small">
                                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                                        Moderate
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                )}

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted small">Total: {total}</div>
                    <div className="d-flex gap-2">
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Prev
                        </Button>
                        <div className="small align-self-center">
                            {page} / {pages}
                        </div>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={page >= pages}
                            onClick={() => setPage((p) => Math.min(pages, p + 1))}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>

            {/* Moderation Modal */}
            <Modal show={!!editing} onHide={closeEdit} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">Moderate Submission #{editing?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editing && (
                        <div className="small">
                            <div className="mb-2 text-muted">
                                <div>
                                    <strong>User:</strong> {editing.user_name || "—"} (UID {editing.user_id})
                                </div>
                                <div>
                                    <strong>AMZ:</strong> {editing.user_amz_name || "—"}
                                </div>
                                <div>
                                    <strong>Date:</strong> {fmtDate(editing.created_at)}
                                </div>
                                <div>
                                    <strong>Platform:</strong> {editing.platform || "—"}
                                </div>
                                <div>
                                    <strong>URL:</strong>{" "}
                                    <a href={editing.post_url} target="_blank" rel="noopener noreferrer">
                                        {editing.post_url}
                                    </a>
                                </div>
                            </div>

                            {editing.screenshot_path && (
                                <div className="mb-3">
                                    <div className="text-muted">Screenshot:</div>
                                    <div className="border rounded-2 p-1 bg-light" style={{ maxWidth: 360 }}>
                                        <img
                                            src={`${process.env.REACT_APP_API_URL}/promo_bounty/${editing.screenshot_path}`}
                                            alt="submission screenshot"
                                            className="img-fluid rounded-2"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="d-flex gap-3 mb-2">
                                <div>
                                    <div className="text-muted">Claimed</div>
                                    <div>
                                        <Badge bg={editing.is_active_link_claimed ? "success" : "secondary"} className="me-1">
                                            Link {editing.is_active_link_claimed ? "Yes" : "No"}
                                        </Badge>
                                        <Badge bg={editing.is_video ? "primary" : "secondary"}>
                                            Video {editing.is_video ? "Yes" : "No"}
                                        </Badge>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted">Verified</div>
                                    <div className="d-flex gap-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="verifyLink"
                                            label={<span className="small">Active link to virtalibry.com</span>}
                                            className="custom-checkbox"
                                            checked={!!editing.verified_active_link}
                                            onChange={(e) => setEditing((s) => ({ ...s, verified_active_link: e.target.checked }))}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="verifyVideo"
                                            label={<span className="small">Video authorship verified</span>}
                                            className="custom-checkbox"
                                            checked={!!editing.verified_video_authorship}
                                            onChange={(e) => setEditing((s) => ({ ...s, verified_video_authorship: e.target.checked }))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row g-2 mb-2">
                                <div className="col-sm-6">
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Estimated payout (read-only)</Form.Label>
                                        <Form.Control size="sm" value={editing.estimated_payout ?? "—"} readOnly />
                                    </Form.Group>
                                </div>
                                <div className="col-sm-6">
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Final payout (override optional)</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            min={0}
                                            placeholder="auto-calc if empty"
                                            value={typeof editing.final_payout === "number" ? editing.final_payout : ""}
                                            onChange={(e) =>
                                                setEditing((s) => ({
                                                    ...s,
                                                    final_payout: e.target.value === "" ? null : Math.max(0, Math.trunc(Number(e.target.value) || 0)),
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            <Form.Group className="mb-2">
                                <Form.Label className="small text-muted mb-1">Moderator note</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    size="sm"
                                    placeholder="Short reasoning / context"
                                    value={editing.moderator_note || ""}
                                    onChange={(e) => setEditing((s) => ({ ...s, moderator_note: e.target.value }))}
                                />
                            </Form.Group>

                            {editing.reviewed_at && <div className="small text-muted">Last reviewed: {fmtDate(editing.reviewed_at)}</div>}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={setPending} disabled={saving}>
                            Set Pending
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={reject} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Reject"}
                        </Button>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" size="sm" onClick={closeEdit}>
                            Close
                        </Button>
                        <Button variant="success" size="sm" onClick={approve} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Approve"}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}
